import { addBreadcrumb } from '@sentry/vue';
import { isProductionEnvironment } from '../env/hostname';
import { redactLinkUrl } from '../utils/redactLinkUrl';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    pendo?: {
      initialize: (opts: {
        visitor: { id: string | number };
        account: { id: string };
      }) => void;
      track: (eventName: string, metadata?: Record<string, unknown>) => void;
      [key: string]: unknown;
    };
  }
}

const GA4_SCRIPT_ATTR = 'data-ga4-loader';
const PENDO_SCRIPT_ATTR = 'data-pendo-loader';

/**
 * Kill switch for Pendo (quick-260709-gxn). While true, `loadPendoScript` never
 * injects the agent, so `window.pendo` stays undefined and every Pendo
 * touchpoint (initialize + all track calls, each guarded on `window.pendo`)
 * no-ops — nothing is sent to Pendo. Flip to `false` to re-enable; the full
 * loader implementation below is retained.
 */
const PENDO_DISABLED = true;

/**
 * Inject the Google gtag.js script for the given measurement ID and call
 * gtag('config', id). Idempotent — multiple calls only inject the script tag once,
 * but each call registers a config event for the provided measurement ID.
 */
export function loadGA4Script(measurementId: string): void {
  if (!measurementId) return;
  if (typeof window === 'undefined') return;
  if (!isProductionEnvironment()) return;

  // Tenant analytics ids in the Analytics.google_analytics_id customization
  // occasionally carry incidental surrounding whitespace (e.g. " G-RLPVF3QM2G").
  // Trim before both validating and using the id, so a valid `G-…` isn't
  // rejected and a trailing space can't corrupt the gtag URL.
  const id = measurementId.trim();

  // GA4 measurement IDs are always `G-…`. A legacy Universal Analytics ID
  // (`UA-…`, deprecated since July 2023) or any other value is a tenant-config
  // data anomaly: gtag.js handed a non-GA4 id pulls the legacy
  // google-analytics.com/analytics.js, which the CSP in index.html deliberately
  // omits from script-src — so the request is guaranteed to be blocked. Skip
  // injecting a script that can only fail. The Analytics.google_analytics_id
  // field is admin-editable and legitimately holds bad/legacy values, so this
  // is expected data variability, not an exceptional condition — leave a
  // breadcrumb (context for any nearby error) + console.warn rather than a
  // captureMessage, which would spawn a per-pageview Sentry issue. Bad values
  // are caught proactively via periodic tenant-config audits instead.
  if (!id.startsWith('G-')) {
    addBreadcrumb({
      category: 'analytics',
      level: 'warning',
      message: `loadGA4Script ignored non-GA4 measurement ID: ${measurementId}`,
    });

    console.warn(
      `loadGA4Script ignored non-GA4 measurement ID: ${measurementId}`,
    );
    return;
  }

  // Set up dataLayer + gtag stub before the script loads.
  // GA4's real script expects Arguments objects in dataLayer, not plain arrays —
  // use the canonical stub pattern so queued pre-load entries are read correctly.
  window.dataLayer = window.dataLayer ?? [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };
  }

  // Push js init timestamp and config event for this measurement ID
  window.gtag('js', new Date());
  // The automatic first page_view reports page_location; a link page's URL
  // carries its token, so send the redacted one.
  window.gtag('config', id, {
    page_location: redactLinkUrl(window.location.href),
  });

  // Inject the script tag once (idempotent by attribute)
  if (document.querySelector(`script[${GA4_SCRIPT_ATTR}]`)) return;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  script.setAttribute(GA4_SCRIPT_ATTR, 'true');
  document.head.appendChild(script);
}

/**
 * Inject the Pendo agent loader. Pendo's loader expects window.pendo to exist
 * as a queue of API call stubs before the real agent script replaces it.
 * After this call, useAuthStore.login() can safely call window.pendo.initialize().
 * Idempotent — second call is a no-op.
 */
export function loadPendoScript(apiKey: string): void {
  // Pendo sending disabled — see PENDO_DISABLED above. With the agent never
  // injected, `window.pendo` stays undefined and no visitor/account init, page
  // loads, guides, or track events reach Pendo. Implementation below is retained.
  if (PENDO_DISABLED) return;

  /* v8 ignore start -- retained-but-dormant loader body; unreachable while
     PENDO_DISABLED is true (quick-260709-gxn). Re-covered when re-enabled. */
  if (!apiKey) return;
  if (typeof window === 'undefined') return;
  // WR-07: match the production gate that loadGA4Script already enforces.
  // Without this, the Pendo agent injects on .blue./dev hosts, contaminating
  // production analytics and pulling dev sessions into Pendo recordings.
  if (!isProductionEnvironment()) return;
  if (document.querySelector(`script[${PENDO_SCRIPT_ATTR}]`)) return;

  // Standard Pendo agent install pattern (per Pendo docs):
  // create stub queue then load the real script which replaces window.pendo.
  const w = window as unknown as Record<string, unknown>;
  if (!w.pendo) {
    const pendoQueue: { _q: unknown[][]; [k: string]: unknown } = { _q: [] };
    const apiNames = [
      'initialize',
      'identify',
      'updateOptions',
      'pageLoad',
      'track',
    ];
    apiNames.forEach((name) => {
      pendoQueue[name] = function (...args: unknown[]) {
        pendoQueue._q.push([name, ...args]);
      };
    });
    w.pendo = pendoQueue;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
  script.setAttribute(PENDO_SCRIPT_ATTR, 'true');
  document.head.appendChild(script);
  /* v8 ignore stop */
}
